import { FormRow } from 'components/common/Forms/Forms.styled';
import Input from 'components/common/Forms/Input';
import { ReactElement, useMemo } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { getHookFormTyped } from 'utils/helpers';
import { UseFormRegister } from 'react-hook-form/dist/types/form';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';
import { useUserValidationRules } from '../../../hooks/patient/useUserValidationRules';
import { TrustData } from '../../../types/trusts';
import { DateOnlyISOString } from '../../../utils/dateUtil';
import {
  NhsPostcodeDisabilitiesForm,
  PatientNhsPostcodeDisabilitiesData,
} from './PatientNhsPostcodeDisabilitiesData';
import CommonSelect from '../../../components/common/Forms/Select';
import { SelectOption } from '../../../types/common';
import { LABEL_BEFORE } from '../../../styles/fontsStyleUtils';
import { FormDesc } from '../../../components/views-components/un-auth/SignupForm/SignupForm.styled';
import { InputPhoneNumber } from '../../../components/common/Forms/InputPhoneNumber';
import { DatePickerInput } from '../../../components/common/Forms/DatePickerInput';
import { SiteSettings } from '../../../types/sites';
import { isGpSurgeryRequired } from '../../../utils/siteUtils';
import { useSiteValidationRules } from '../../../hooks/patient/useUserSiteValidationRules';

type SubFormType = {
  birthdate?: DateOnlyISOString;
  firstname: string;
  lastname: string;
  phone?: string;
} & NhsPostcodeDisabilitiesForm;

export interface PatientFormProps<T extends SubFormType> {
  register: UseFormRegister<T>;
  control: Control<T>;
  errors: FieldErrors<T>;
  trusts: TrustData[];
  siteSettings: SiteSettings;
  hideEmail: boolean;
  gpSurgeriesOptions?: SelectOption[];
}

export const PatientProfileOrMemberFormData = <T extends SubFormType>({
  trusts,
  siteSettings,
  hideEmail,
  gpSurgeriesOptions,
  ...rest
}: PatientFormProps<T>): ReactElement => {
  const { register, control, errors } = getHookFormTyped<SubFormType, T>(rest);
  const { t } = useCommonTranslation();
  const {
    birthdateRules,
    phoneRules,
    surnameRules,
    firstnameRules,
    emailRules,
  } = useUserValidationRules({
    emailRequired: true,
  });
  const { gpRules } = useSiteValidationRules({
    siteSettings,
  });

  const otherGPName = useMemo(
    () =>
      gpSurgeriesOptions?.find(
        (option) => option.value === trusts[0].unknown_gp_surgery_id,
      )?.label,
    [gpSurgeriesOptions, trusts],
  );

  return (
    <>
      <FormRow>
        <Input
          label={t('first-name')}
          placeholder={t('first-name')}
          helperText={errors.firstname?.message}
          hasError={Boolean(errors.firstname)}
          {...register('firstname', firstnameRules)}
          required
          autoComplete="given-name"
        />
        <Input
          label={t('surname')}
          placeholder={t('surname')}
          helperText={errors.lastname?.message}
          hasError={Boolean(errors.lastname)}
          {...register('lastname', surnameRules)}
          required
          autoComplete="family-name"
        />
      </FormRow>
      {!hideEmail && (
        <FormRow>
          <Input
            label={t('email')}
            placeholder={t('email')}
            helperText={errors.email?.message}
            hasError={Boolean(errors.email)}
            {...register('email', emailRules)}
            required
            autoComplete="email"
          />
        </FormRow>
      )}
      <FormRow>
        <Controller
          control={control}
          name="birthdate"
          rules={birthdateRules}
          render={({ field }) => {
            return (
              <DatePickerInput
                name="birthdate"
                label={t('date-of-birth')}
                placeholder={t('date-of-birth')}
                onChange={(value) => {
                  field.onChange(value);
                }}
                id={'new_birthdate'}
                maxDate={new Date()}
                hasError={Boolean(errors.birthdate)}
                helperText={errors.birthdate?.message}
                value={field.value}
                required
                autoComplete="bday"
              />
            );
          }}
        />
        <Controller
          name="phone"
          control={control}
          rules={phoneRules}
          render={({ field }) => (
            <InputPhoneNumber
              label={t('phone-number')}
              placeholder={t('phone-number')}
              {...field}
              required={!!phoneRules.required}
              helperText={errors.phone?.message}
              hasError={Boolean(errors?.phone)}
              autoComplete="tel"
            />
          )}
        />
      </FormRow>
      <PatientNhsPostcodeDisabilitiesData
        register={register}
        control={control}
        errors={errors}
        siteSettings={siteSettings}
      />
      {isGpSurgeryRequired(siteSettings) && (
        <>
          <FormDesc
            style={{
              padding: '0 var(--s1)',
            }}
          >
            {t('general-practice-help', {
              other_gp_label: otherGPName,
            })}
          </FormDesc>
          <FormRow>
            <Controller
              name="gp_surgery_id"
              render={({ field }) => (
                <CommonSelect
                  {...field}
                  labelPosition={LABEL_BEFORE}
                  label={t('general-practice')}
                  placeholder={t('general-practice-placeholder')}
                  disabled={!gpSurgeriesOptions}
                  options={gpSurgeriesOptions}
                  helperText={errors.gp_surgery_id?.message}
                  hasError={Boolean(errors.gp_surgery_id)}
                  onChange={(val) => {
                    field.onChange(val);
                  }}
                  required
                />
              )}
              control={control}
              rules={gpRules}
            />
          </FormRow>
        </>
      )}
    </>
  );
};
